import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import { apiErrorText, type ApiErrorTextOptions } from "@/lib/api-error";
import { platformLogin, platformResendCode, platformVerify } from "@/services/api/platform-auth";

/** Platform codes first, then the shared sign-in wording (OTP_*, LOGIN_LOCKED). */
export const PLATFORM_AUTH_ERROR_OPTIONS: ApiErrorTextOptions = {
  namespace: ["platformAuth.errors", "auth.errors"],
  fields: { password: "platformAuth.passwordRule" },
};

const inputClass = "w-full rounded-md border border-[var(--border-strong)] px-3 py-2";
const buttonClass =
  "w-full rounded-md bg-[var(--accent-strong)] px-4 py-2 text-[var(--accent-foreground)] hover:bg-[var(--accent-strong-hover)] disabled:opacity-50";

/**
 * MT-1 AUTH-009 (FR-6.2, FR-6.6): the platform console's sign-in — password, then the code mailed
 * through the platform account. On success it stores the platform session (never the school one)
 * and calls `onSignedIn`.
 */
export function PlatformLoginForm({ onSignedIn }: { onSignedIn: () => void }) {
  const t = useTranslations("platformAuth");
  const tRoot = useTranslations();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(step: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await step();
    } catch (err) {
      setError(apiErrorText(err, tRoot, "platformAuth.failed", PLATFORM_AUTH_ERROR_OPTIONS));
    } finally {
      setBusy(false);
    }
  }

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const pending = await platformLogin(email.trim(), password);
      setPassword("");
      setChallengeToken(pending.challengeToken);
    });
  };

  const submitCode = (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeToken) return;
    void run(async () => {
      await platformVerify(challengeToken, code.trim());
      onSignedIn();
    });
  };

  const resend = () => {
    if (!challengeToken) return;
    void run(async () => {
      await platformResendCode(challengeToken);
      setNotice(t("codeResent"));
    });
  };

  const errorLine = error && (
    <p role="alert" className="text-sm text-[var(--danger)]" data-testid="platform-login-error">
      {error}
    </p>
  );

  if (challengeToken) {
    return (
      <form onSubmit={submitCode} className="space-y-4" data-testid="platform-code-form">
        <p className="text-sm">{t("codeSent")}</p>
        <div>
          <label htmlFor="platform-code" className="mb-1 block text-sm font-medium">
            {t("codeLabel")}
          </label>
          <input
            id="platform-code"
            data-testid="platform-code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className={inputClass}
          />
        </div>
        {errorLine}
        {notice && (
          <p role="status" className="text-sm">
            {notice}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className={buttonClass}
          data-testid="platform-code-submit"
        >
          {t("verify")}
        </button>
        <button
          type="button"
          onClick={resend}
          disabled={busy}
          className="w-full text-sm underline"
          data-testid="platform-code-resend"
        >
          {t("resend")}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submitPassword} className="space-y-4" data-testid="platform-login-form">
      <div>
        <label htmlFor="platform-email" className="mb-1 block text-sm font-medium">
          {t("emailLabel")}
        </label>
        <input
          id="platform-email"
          data-testid="platform-email-input"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
      </div>
      <div>
        <label htmlFor="platform-password" className="mb-1 block text-sm font-medium">
          {t("passwordLabel")}
        </label>
        <input
          id="platform-password"
          data-testid="platform-password-input"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputClass}
        />
      </div>
      {errorLine}
      <button
        type="submit"
        disabled={busy}
        className={buttonClass}
        data-testid="platform-login-submit"
      >
        {t("signIn")}
      </button>
      <p className="text-center text-sm">
        <Link to="/platform/forgot-password" className="text-[var(--text-heading)] underline">
          {t("forgotLink")}
        </Link>
      </p>
    </form>
  );
}
